import React, { useEffect, useMemo, useRef, useState } from "react";
import { animate, motion, useInView } from "framer-motion";

const NAVBAR_OFFSET = 72;

const statsConfig = [
  { id: "uptime", kind: "percent", target: 99.99, label: "UPTIME" },
  { id: "support", kind: "ratio", leftTarget: 24, right: "/7", label: "SUPPORT" },
  { id: "nodes", kind: "plus", target: 20, label: "RPC NODES" },
  { id: "latency", kind: "latency", target: 20, label: "AVG. LATENCY" },
];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = () => setReduced(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
}

const StatValue = ({ config, index, isInView, reducedMotion }) => {
  const [percent, setPercent] = useState(0);
  const [ratioLeft, setRatioLeft] = useState(0);
  const [plus, setPlus] = useState(0);
  const [ms, setMs] = useState(0);

  const stagger = index * 0.1;
  const duration = 1.65;
  const ease = useMemo(() => [0.22, 0.61, 0.36, 1], []);

  useEffect(() => {
    if (!isInView) {
      return undefined;
    }

    if (reducedMotion) {
      if (config.kind === "percent") {
        setPercent(config.target);
      } else if (config.kind === "ratio") {
        setRatioLeft(config.leftTarget);
      } else if (config.kind === "plus") {
        setPlus(config.target);
      } else {
        setMs(config.target);
      }
      return undefined;
    }

    const controls = [];

    if (config.kind === "percent") {
      controls.push(
        animate(0, config.target, {
          duration,
          ease,
          delay: stagger,
          onUpdate: (v) => setPercent(v),
        }),
      );
    } else if (config.kind === "ratio") {
      controls.push(
        animate(0, config.leftTarget, {
          duration: duration * 0.85,
          ease,
          delay: stagger,
          onUpdate: (v) => setRatioLeft(Math.round(v)),
        }),
      );
    } else if (config.kind === "plus") {
      controls.push(
        animate(0, config.target, {
          duration,
          ease,
          delay: stagger,
          onUpdate: (v) => setPlus(Math.round(v)),
        }),
      );
    } else {
      controls.push(
        animate(0, config.target, {
          duration,
          ease,
          delay: stagger,
          onUpdate: (v) => setMs(Math.round(v)),
        }),
      );
    }

    return () => {
      controls.forEach((c) => c.stop());
    };
  }, [isInView, reducedMotion, config, stagger, ease]);

  let display = "";
  if (config.kind === "percent") {
    display = `${percent.toFixed(2)}%`;
  } else if (config.kind === "ratio") {
    display = `${ratioLeft}${config.right}`;
  } else if (config.kind === "plus") {
    display = `${plus}+`;
  } else {
    display = `<${ms}ms`;
  }

  return (
    <div className="flex flex-col items-center text-center">
      <motion.span
        className="font-title text-2xl font-bold text-primary sm:text-3xl md:text-5xl tabular-nums tracking-tight"
        initial={{ opacity: 0, y: 8 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
        transition={{
          duration: 0.35,
          delay: reducedMotion ? 0 : stagger,
          ease: "easeOut",
        }}
      >
        {display}
      </motion.span>
      <span className="font-secondary mt-2 text-xl font-medium uppercase tracking-[0.12em] text-white/80">
        {config.label}
      </span>
    </div>
  );
};

const HeroSection = () => {
  const statsRef = useRef(null);
  const isStatsInView = useInView(statsRef, { once: true, amount: 0.35 });
  const reducedMotion = usePrefersReducedMotion();

  const scrollToPricing = (event) => {
    event.preventDefault();
    const section = document.getElementById("pricing");
    if (!section) {
      return;
    }
    const top =
      section.getBoundingClientRect().top + window.scrollY - NAVBAR_OFFSET;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  };

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
        className="font-title flex flex-col gap-4 text-left text-5xl font-bold tracking-tight text-white sm:text-6xl md:text-[72px] lg:text-[80px]"
      >
        <div className="text-white">RPC Blockchain</div>
        <div className="text-primary">Data Nodes</div>
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08, duration: 0.55, ease: "easeOut" }}
        className="font-secondary mt-4 max-w-[560px] text-left text-xl leading-relaxed text-white/65"
      >
        Monthly subscription service to generate your nodes and visualize the
        quality of your data with enterprise grade precision.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.14, duration: 0.5, ease: "easeOut" }}
        className="flex flex-wrap items-center justify-center gap-4"
      >
        <a
          href="/#/signup"
          className="font-secondary inline-flex min-w-[160px] items-center justify-center gap-2 rounded-sm border border-primary bg-primary px-6 py-4 text-[15px] font-bold leading-none text-[#0a0a0a] transition-colors hover:bg-primary-hover"
        >
          Get Started
          <span aria-hidden="true" className="text-lg leading-none">
            →
          </span>
        </a>
        <a
          href="#pricing"
          onClick={scrollToPricing}
          className="font-secondary inline-flex min-w-[140px] items-center justify-center rounded-sm border-white/20 bg-[#0c0c0c] px-6 py-4 text-[15px] font-semibold text-white transition-colors hover:border-white/35 hover:bg-white/[0.06]"
        >
          Learn more
        </a>
      </motion.div>

      <motion.div
        ref={statsRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.22, duration: 0.55, ease: "easeOut" }}
        className="mt-36 grid w-full grid-cols-2 gap-8 sm:grid-cols-4 sm:gap-6"
      >
        {statsConfig.map((config, index) => (
          <StatValue
            key={config.id}
            config={config}
            index={index}
            isInView={isStatsInView}
            reducedMotion={reducedMotion}
          />
        ))}
      </motion.div>
    </div>
  );
};

export default HeroSection;
